import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, Upload, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

function FileUpload({ label, hint, testid, url, onChange, required, t }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error(t("kyc.file_too_big"));
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      onChange(`${backendUrl}${data.url}`);
      toast.success(t("kyc.file_uploaded"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("kyc.file_upload_fail"));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2">
        {label}{required && <span className="text-[#C85A32]">*</span>}
      </Label>
      {hint && <p className="text-xs text-[#4B5563] leading-relaxed">{hint}</p>}
      <div className="flex items-center gap-3">
        <label className="flex-1 cursor-pointer border-2 border-dashed border-[#1A3322]/30 rounded-lg px-4 py-6 hover:border-[#C85A32] transition text-center" data-testid={testid}>
          <input type="file" accept="image/*,.pdf" className="hidden" onChange={upload} disabled={busy}/>
          {busy ? (
            <span className="inline-flex items-center gap-2 text-sm text-[#4B5563]"><Loader2 className="w-4 h-4 animate-spin"/> {t("kyc.uploading")}</span>
          ) : url ? (
            <span className="inline-flex items-center gap-2 text-sm text-green-700 font-semibold"><ShieldCheck className="w-4 h-4"/> {t("kyc.file_replace")}</span>
          ) : (
            <span className="inline-flex items-center gap-2 text-sm text-[#4B5563]"><Upload className="w-4 h-4"/> {t("kyc.drop_file")}</span>
          )}
        </label>
        {url && <a href={url} target="_blank" rel="noreferrer" className="text-xs text-[#1A3322] underline">{t("kyc.view")}</a>}
      </div>
    </div>
  );
}

export default function SellerVerification() {
  const { user, loading } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [initial, setInitial] = useState(null);
  const [f, setF] = useState({
    seller_type: "individual",
    full_legal_name: "",
    date_of_birth: "",
    address: "",
    city: "Kinshasa",
    country: "RDC",
    phone: "",
    id_doc_type: "id_card",
    id_doc_number: "",
    id_doc_url: "",
    selfie_url: "",
    proof_url: "",
    payout_method: "mobile_money",
    payout_holder: "",
    payout_number: "",
    business_name: "",
    rccm: "",
    tax_id: "",
  });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await api.get("/kyc/me");
      setInitial(data);
      if (data.submission) {
        setF((x) => ({ ...x, ...data.submission }));
      } else {
        try {
          const raw = sessionStorage.getItem("kyc_prefill");
          if (raw) {
            const pre = JSON.parse(raw);
            setF((x) => ({
              ...x,
              seller_type: pre.seller_type || x.seller_type,
              full_legal_name: pre.full_legal_name || x.full_legal_name,
              phone: pre.phone || x.phone,
              address: pre.address || x.address,
              city: pre.city || x.city,
              country: pre.country || x.country,
              business_name: pre.business_name || x.business_name,
              rccm: pre.rccm || x.rccm,
              tax_id: pre.tax_id || x.tax_id,
            }));
            sessionStorage.removeItem("kyc_prefill");
          }
        } catch (e) { console.warn("KYC prefill parse failed:", e?.message); }
      }
    })();
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/connexion"/>;
  if (user.role !== "seller" && user.role !== "admin") return <Navigate to="/"/>;

  const submit = async (e) => {
    e.preventDefault();
    if (!f.id_doc_url || !f.selfie_url) return toast.error(t("kyc.err_docs"));
    if (f.seller_type === "business" && !f.rccm) return toast.error(t("kyc.err_rccm"));
    setSubmitting(true);
    try {
      await api.post("/kyc/submit", f);
      toast.success(t("kyc.sent"));
      nav("/vendeur");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("kyc.err_generic"));
    } finally { setSubmitting(false); }
  };

  const status = initial?.status || "not_started";
  const readonly = status === "pending" || status === "verified";

  return (
    <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="kyc-page">
      <div className="mb-8">
        <Link to="/vendeur" className="text-sm text-[#4B5563] hover:underline">{t("kyc.back")}</Link>
        <h1 className="heading font-extrabold text-3xl sm:text-4xl mt-3 flex items-center gap-3">
          <ShieldCheck className="w-8 h-8 text-[#C85A32]"/> {t("kyc.title")}
        </h1>
        <p className="text-[#4B5563] mt-2 text-sm">
          {t("kyc.intro")}
        </p>
      </div>

      {status === "pending" && (
        <div className="mb-6 rounded-lg border-2 border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="status-pending">
          <ShieldCheck className="w-5 h-5 text-amber-700 shrink-0 mt-0.5"/>
          <div className="text-sm text-amber-900">
            <p className="font-semibold">{t("kyc.pending_title")}</p>
            <p>{t("kyc.pending_desc")}</p>
          </div>
        </div>
      )}
      {status === "verified" && (
        <div className="mb-6 rounded-lg border-2 border-green-200 bg-green-50 p-4 flex items-start gap-3" data-testid="status-verified">
          <ShieldCheck className="w-5 h-5 text-green-700 shrink-0 mt-0.5"/>
          <div className="text-sm text-green-900">
            <p className="font-semibold">{t("kyc.verified_title")}</p>
            <p>{t("kyc.verified_desc")}</p>
          </div>
        </div>
      )}
      {status === "rejected" && initial?.rejection_reason && (
        <div className="mb-6 rounded-lg border-2 border-red-200 bg-red-50 p-4 flex items-start gap-3" data-testid="status-rejected">
          <AlertTriangle className="w-5 h-5 text-red-700 shrink-0 mt-0.5"/>
          <div className="text-sm text-red-900">
            <p className="font-semibold">{t("kyc.rejected_title")}</p>
            <p>{t("kyc.rejected_reason")} <em>{initial.rejection_reason}</em></p>
            <p className="mt-1">{t("kyc.rejected_fix")}</p>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="space-y-8" data-testid="kyc-form">

        {/* Section 1 — Identity */}
        <section className="bg-white border border-[#1A3322]/10 rounded-lg p-6 space-y-4">
          <h2 className="heading font-bold text-lg text-[#1A3322]">{t("kyc.section1")}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("kyc.seller_type")}</Label>
              <Select value={f.seller_type} onValueChange={(v) => set("seller_type", v)} disabled={readonly}>
                <SelectTrigger data-testid="seller-type"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">{t("kyc.individual")}</SelectItem>
                  <SelectItem value="business">{t("kyc.business")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("kyc.full_legal_name")}</Label>
              <Input required value={f.full_legal_name} onChange={(e) => set("full_legal_name", e.target.value)} disabled={readonly} data-testid="full-legal-name" placeholder={t("kyc.full_legal_placeholder")}/>
            </div>
            <div className="space-y-2">
              <Label>{t("kyc.dob")}</Label>
              <Input required type="date" value={f.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} disabled={readonly} data-testid="date-of-birth"/>
            </div>
            <div className="space-y-2">
              <Label>{t("kyc.phone")}</Label>
              <Input required value={f.phone} onChange={(e) => set("phone", e.target.value)} disabled={readonly} data-testid="phone" placeholder={t("kyc.phone_placeholder")}/>
            </div>
          </div>
        </section>

        {/* Section 2 — Address */}
        <section className="bg-white border border-[#1A3322]/10 rounded-lg p-6 space-y-4">
          <h2 className="heading font-bold text-lg text-[#1A3322]">{t("kyc.section2")}</h2>
          <div className="space-y-2">
            <Label>{t("kyc.address")}</Label>
            <Textarea required rows={2} value={f.address} onChange={(e) => set("address", e.target.value)} disabled={readonly} data-testid="address" placeholder={t("kyc.address_placeholder")}/>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("kyc.city")}</Label>
              <Input required value={f.city} onChange={(e) => set("city", e.target.value)} disabled={readonly} data-testid="city"/>
            </div>
            <div className="space-y-2">
              <Label>{t("kyc.country")}</Label>
              <Input required value={f.country} onChange={(e) => set("country", e.target.value)} disabled={readonly} data-testid="country"/>
            </div>
          </div>
        </section>

        {/* Section 3 — Business (only if applicable) */}
        {f.seller_type === "business" && (
          <section className="bg-white border border-[#1A3322]/10 rounded-lg p-6 space-y-4" data-testid="business-section">
            <h2 className="heading font-bold text-lg text-[#1A3322]">{t("kyc.section_business")}</h2>
            <div className="space-y-2">
              <Label>{t("kyc.business_name")}</Label>
              <Input required value={f.business_name} onChange={(e) => set("business_name", e.target.value)} disabled={readonly} data-testid="business-name"/>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("kyc.rccm")}</Label>
                <Input required value={f.rccm} onChange={(e) => set("rccm", e.target.value)} disabled={readonly} data-testid="rccm" placeholder={t("kyc.rccm_placeholder")}/>
              </div>
              <div className="space-y-2">
                <Label>{t("kyc.tax_id")}</Label>
                <Input value={f.tax_id} onChange={(e) => set("tax_id", e.target.value)} disabled={readonly} data-testid="tax-id"/>
              </div>
            </div>
          </section>
        )}

        {/* Section 4 — Documents */}
        <section className="bg-white border border-[#1A3322]/10 rounded-lg p-6 space-y-4">
          <h2 className="heading font-bold text-lg text-[#1A3322]">{f.seller_type === "business" ? t("kyc.section_docs_biz") : t("kyc.section_docs_ind")}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("kyc.id_doc_type")}</Label>
              <Select value={f.id_doc_type} onValueChange={(v) => set("id_doc_type", v)} disabled={readonly}>
                <SelectTrigger data-testid="id-doc-type"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="id_card">{t("kyc.id_doc_id_card")}</SelectItem>
                  <SelectItem value="passport">{t("kyc.id_doc_passport")}</SelectItem>
                  <SelectItem value="driver_license">{t("kyc.id_doc_permit")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("kyc.id_doc_number")}</Label>
              <Input required value={f.id_doc_number} onChange={(e) => set("id_doc_number", e.target.value)} disabled={readonly} data-testid="id-doc-number"/>
            </div>
          </div>
          {!readonly && (
            <>
              <FileUpload
                t={t}
                label={t("kyc.upload_id_label")}
                hint={t("kyc.upload_id_hint")}
                testid="upload-id-doc"
                url={f.id_doc_url}
                onChange={(u) => set("id_doc_url", u)}
                required
              />
              <FileUpload
                t={t}
                label={t("kyc.upload_selfie_label")}
                hint={t("kyc.upload_selfie_hint")}
                testid="upload-selfie"
                url={f.selfie_url}
                onChange={(u) => set("selfie_url", u)}
                required
              />
              <FileUpload
                t={t}
                label={f.seller_type === "business" ? t("kyc.upload_proof_biz_label") : t("kyc.upload_proof_ind_label")}
                hint={f.seller_type === "business" ? t("kyc.upload_proof_biz_hint") : t("kyc.upload_proof_ind_hint")}
                testid="upload-proof"
                url={f.proof_url}
                onChange={(u) => set("proof_url", u)}
              />
            </>
          )}
          {readonly && (
            <div className="text-sm text-[#4B5563] flex flex-wrap gap-4">
              {f.id_doc_url && <a href={f.id_doc_url} target="_blank" rel="noreferrer" className="underline">{t("kyc.id_doc_view")}</a>}
              {f.selfie_url && <a href={f.selfie_url} target="_blank" rel="noreferrer" className="underline">{t("kyc.selfie_view")}</a>}
              {f.proof_url && <a href={f.proof_url} target="_blank" rel="noreferrer" className="underline">{t("kyc.proof_view")}</a>}
            </div>
          )}
        </section>

        {/* Section 5 — Payout account */}
        <section className="bg-white border border-[#1A3322]/10 rounded-lg p-6 space-y-4">
          <h2 className="heading font-bold text-lg text-[#1A3322]">{f.seller_type === "business" ? t("kyc.section_payout_biz") : t("kyc.section_payout_ind")}</h2>
          <p className="text-xs text-[#4B5563]">{t("kyc.payout_note_pre")} <strong>{t("kyc.payout_note_strong")}</strong> {t("kyc.payout_note_post")}</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("kyc.method")}</Label>
              <Select value={f.payout_method} onValueChange={(v) => set("payout_method", v)} disabled={readonly}>
                <SelectTrigger data-testid="payout-method"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mobile_money">{t("kyc.method_mm")}</SelectItem>
                  <SelectItem value="bank">{t("kyc.method_bank")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("kyc.holder")}</Label>
              <Input required value={f.payout_holder} onChange={(e) => set("payout_holder", e.target.value)} disabled={readonly} data-testid="payout-holder" placeholder={t("kyc.holder_placeholder")}/>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>{f.payout_method === "mobile_money" ? t("kyc.number_mm") : t("kyc.number_bank")}</Label>
              <Input required value={f.payout_number} onChange={(e) => set("payout_number", e.target.value)} disabled={readonly} data-testid="payout-number"/>
            </div>
          </div>
        </section>

        {!readonly && (
          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" disabled={submitting} className="pill-btn bg-[#1A3322] hover:bg-[#24472F] rounded-full h-12 px-6" data-testid="kyc-submit">
              {submitting ? t("kyc.submitting") : t("kyc.submit")}
            </Button>
            <p className="text-xs text-[#4B5563]">{t("kyc.submit_hint")}</p>
          </div>
        )}
      </form>
    </div>
  );
}
